import React from 'react';
import { Table, Tag } from 'antd';
import {
  PAGE_SIZE_OPTIONS,
  SCROLL_Y,
  COLUMN_LARGE,
  COLUMN_MEDIUM,
  COLUMN_PRE_LARGE,
  COLUMN_EXTRA_LARGE,
  COLUMN_SMALL,
} from 'constants/table';
import { checkValue, formatDate } from 'utils';
import { filterColumns, priceRender, renderWithTooltip } from 'utils/table';
import { SumRange } from 'components/Filters/SumRange';
import { InputFilter } from 'components/Filters/InputFilter';
import { DatePickerFilter } from 'components/Filters/DatePickerFilter';
import { CheckboxFilter } from 'components/Filters/CheckboxFilter';
import { FilterIcon } from 'components/Filters/FilterIcon';
import {
  positionList,
  statusList,
  positionMap,
  statusMap,
  disabilityMap,
  citizenshipMap,
} from '../datasource';

function EmployeesTable({
  items,
  current,
  pageSize,
  loading,
  totalRows,
  allowFilter = false,
  filters = {},
  onRowClick,
  onChange,
  onFilter,
}) {
  const { full_name, iin, f_salary, t_salary, position, date_in, status } =
    filters;

  const columns = [
    {
      fixed: 'left',
      width: COLUMN_PRE_LARGE,
      title: 'ФИО',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (value) => renderWithTooltip(value),
      filterDropdown: () => (
        <InputFilter
          value={full_name}
          placeholder="Поиск по ФИО"
          onFilterChange={(full_name) => onFilter({ full_name })}
        />
      ),
      filterIcon: () => <FilterIcon search isActive={full_name} />,
    },
    {
      width: COLUMN_MEDIUM,
      title: 'ИИН',
      dataIndex: 'iin',
      key: 'iin',
      render: checkValue,
      filterDropdown: () => (
        <InputFilter
          value={iin}
          placeholder="Поиск по ИИН"
          onFilterChange={(iin) => onFilter({ iin })}
        />
      ),
      filterIcon: () => <FilterIcon search isActive={iin} />,
    },
    {
      width: COLUMN_SMALL,
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (value) => {
        const color = value === 'work' ? 'green' : 'red';

        return <Tag color={color}>{statusMap[value]}</Tag>;
      },
      filterDropdown: () => {
        return (
          <CheckboxFilter
            filter={status}
            filters={statusList}
            onFilterChange={(status) => onFilter({ status })}
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={status} />,
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Гражданство',
      dataIndex: 'citizenship',
      key: 'citizenship',
      render: (value) => citizenshipMap[value],
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Инвалидность',
      dataIndex: 'disability',
      key: 'disability',
      render: (value) => disabilityMap[value],
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Должность',
      dataIndex: 'position',
      key: 'position',
      render: (value) => positionMap[value],
      filterDropdown: () => {
        return (
          <CheckboxFilter
            filter={position}
            filters={positionList}
            onFilterChange={(position) => onFilter({ position })}
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={position} />,
    },
    {
      width: COLUMN_LARGE,
      title: 'Зарплата (₸)',
      dataIndex: 'current_salary',
      key: 'current_salary',
      render: priceRender,
      filterDropdown: () => {
        return (
          <SumRange
            fromValue={f_salary}
            toValue={t_salary}
            getRange={(first, second) =>
              onFilter({ f_salary: first, t_salary: second })
            }
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={f_salary || t_salary} />,
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Вычет МЗП',
      dataIndex: 'tax_deduction',
      key: 'tax_deduction',
      render: (value) => (value ? 'Есть' : 'Нету'),
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Дата приема',
      dataIndex: 'date',
      key: 'date',
      render: (value) => formatDate(value),
      filterDropdown: () => {
        return (
          <DatePickerFilter
            value={date_in}
            getDate={(date_in) => onFilter({ date_in })}
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={date_in} />,
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Дата выхода на пенсию',
      dataIndex: 'pensioner_date',
      key: 'pensioner_date',
      render: (pensioner_date) => {
        if (pensioner_date) {
          return formatDate(pensioner_date);
        }

        return '---';
      },
    },
    {
      width: COLUMN_EXTRA_LARGE,
      title: 'Клиент',
      dataIndex: 'company',
      key: 'company',
      render: (name) => renderWithTooltip(name, 60),
    },
  ];

  return (
    <Table
      columns={filterColumns(columns, allowFilter)}
      dataSource={items}
      loading={loading}
      scroll={{ y: SCROLL_Y }}
      pagination={{
        current,
        pageSize,
        total: totalRows,
        showSizeChanger: true,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
      }}
      rowKey="id"
      tableLayout="fixed"
      onRow={(record) => {
        if (onRowClick) {
          return {
            onDoubleClick: () => onRowClick(record),
          };
        }
        return null;
      }}
      onChange={onChange}
    />
  );
}

export { EmployeesTable };
